import { ShopifyLocationDto } from "@babackapp/private-api-client/types";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTotalDiscount } from "./hooks/use-total-discount";
import { useRefundSelectedArticles } from "./hooks/use-total-to-refund";

type RefundContextType = {
  doNotChargeForRefund: boolean;
  doNotRefundInitialShipping: boolean;
  forceRefundInitialShipping: boolean;
  setDoNotChargeForRefund: (doNotChargeForRefund: boolean) => void;
  setDoNotRefundInitialShipping: (doNotRefundInitialShipping: boolean) => void;
  setForceRefundInitialShipping: (forceRefundInitialShipping: boolean) => void;

  discountAmount: number;
  refundAmount: number | null;
  setDiscountAmount: (amount: number) => void;
  setRefundAmount: (amount: number) => void;

  restockLocation: ShopifyLocationDto | null;
  setRestockLocation: (location: ShopifyLocationDto | null) => void;

  bypassStock: boolean;
  setBypassStock: (byPassStock: boolean) => void;
};

const RefundContext = createContext<RefundContextType | undefined>(undefined);

export const RefundProvider: FC<PropsWithChildren> = ({ children }) => {
  const [restockLocation, setRestockLocation] =
    useState<ShopifyLocationDto | null>(null);

  const [doNotChargeForRefund, setDoNotChargeForRefund] =
    useState<boolean>(false);

  const [doNotRefundInitialShipping, setDoNotRefundInitialShipping] =
    useState<boolean>(false);

  const [forceRefundInitialShipping, setForceRefundInitialShipping] =
    useState<boolean>(false);

  const [bypassStock, setBypassStock] = useState<boolean>(false);

  const computedDiscountAmount = useTotalDiscount({
    withCharges: true,
    chargeForRefund: !doNotChargeForRefund,
    chargeForInitialShipping: !doNotRefundInitialShipping,
    forceRefundInitialShipping: forceRefundInitialShipping,
  });
  const computedRefundAmount = useRefundSelectedArticles({
    withCharges: true,
    chargeForRefund: !doNotChargeForRefund,
    chargeForInitialShipping: !doNotRefundInitialShipping,
    forceRefundInitialShipping: forceRefundInitialShipping,
  });

  const [discountAmount, setDiscountAmount] = useState<number>(
    computedDiscountAmount,
  );
  const [refundAmount, setRefundAmount] = useState<number | null>(
    computedRefundAmount,
  );

  useEffect(() => {
    setDiscountAmount(computedDiscountAmount);
    setRefundAmount(computedRefundAmount);
  }, [computedDiscountAmount, computedRefundAmount]);

  return (
    <RefundContext.Provider
      value={{
        doNotChargeForRefund,
        doNotRefundInitialShipping,
        forceRefundInitialShipping,
        setDoNotChargeForRefund,
        setDoNotRefundInitialShipping,
        setForceRefundInitialShipping,
        discountAmount,
        refundAmount,
        setDiscountAmount,
        setRefundAmount,
        restockLocation,
        setRestockLocation,
        bypassStock: bypassStock,
        setBypassStock: setBypassStock,
      }}
    >
      {children}
    </RefundContext.Provider>
  );
};

export const useRefundContext = () => {
  const context = useContext(RefundContext);

  if (context === undefined) {
    throw new Error("useRefundContext must be used within a RefundContext");
  }

  return context;
};
